import { Box } from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { useFeeds } from "../../../../utils/reactQuery/feeds";
import { useBoardName } from "../../../../utils/reactQuery/boards";
import Loader from "../../../../components/Loader";
import OpenCardDetail from "../Kanban/openCardDetail";
import FeedItemComponent from "./FeedItemComponent";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";

const HistoryItems = () => {
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;
  const [allData, setAllData] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const { id } = useBoardIdByKey();
  const { boardName } = useBoardName(id);
  const cardData = searchParams.get("card_id");

  const dateParams = useMemo(() => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      const parsedDate = JSON.parse(decodeURIComponent(dateParam));
      const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toISOString().slice(0, 19);
      };
      return {
        start_date: formatDate(parsedDate.startDate),
        end_date: formatDate(parsedDate.endDate),
      };
    }
    return { start_date: null, end_date: null };
  }, [searchParams]);
  
  const user_ids = useMemo(() => {
    const searchParam = searchParams.get("search");
    if (searchParam) {
      return searchParam
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id));
    }
    return null;
  }, [searchParams]);
  
  const filters = useMemo(() => ({
    milestoneId: searchParams.get("milestone"),
    user_ids,
    start_date: dateParams.start_date,
    end_date: dateParams.end_date,
  }), [searchParams, dateParams, user_ids]);

  const { feeds, isLoading, isFetching } = useFeeds(id, currentPage, itemsPerPage, filters);

  const mapFeedItem = useCallback((item) => {
    if (item.history_type === "Comment" && (
      item.response_object?.event_type === "create_comment" ||
      item.response_object?.event_type === "update_comment" ||
      item.response_object?.event_type === "delete_comment")) {
      return {
        ...item,
        content: "comment_content",
        id: item.history_id,
      };
    }
    if (item.history_type === "Card" && item.response_object?.event_type === "card_log_created") {
      const newKey = item.response_object?.new_key || {};
      return {
        ...item,
        content: "Worklog_content",
        id: item.history_id,
        card_id: item.card_id,
        work_performed: newKey.work_performed,
        hour_spent: newKey.hours_spent,
        allocated_hours: newKey.allocated_hours,
        workflow_step: newKey.work_log_step,
        log_type: newKey.log_type,
        created_at: item.at_time,
        username: item.response_object.user_object?.username,
        user_image: item.response_object.user_object?.user_image,
        user_profile_color: item.response_object.user_object?.profile_color,
      };
    }
    return { content: "history_content", ...item };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setAllData([]);
    setHasMore(true);
  }, [filters.milestoneId, filters.start_date, filters.end_date, filters.user_ids]);

  useEffect(() => {
    const newData = feeds?.data?.data?.responseData?.map(mapFeedItem) || [];
    const totalCount = feeds?.data?.data?.totalRecord || 0;
    if (currentPage === 1) {
      setAllData(newData);
    } else {
      setAllData(prev => [...prev, ...newData]);
    }
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    setHasMore(currentPage < totalPages);
  }, [feeds]);

  // Infinite scroll observer
  const { ref: bottomRef, inView } = useInView({
    threshold: 0,
    rootMargin: '0px 0px 25% 0px',
  });

  useEffect(() => {
    if (inView && !isFetching && hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  }, [inView, isFetching, hasMore]);

  if (isLoading && currentPage === 1) {
    return <div className="loader-center"><Loader color="black" /></div>;
  }

  return (
    <>
      {cardData && <OpenCardDetail />}
      {allData.length > 0 ? (
        <Box className="feed-outer-container">
          <Box overflow="auto" flexGrow={1}>
            {allData.map(item => (
              <FeedItemComponent
               key={item.id}
               item={item}
               boardName={boardName}
             />
            ))}
          </Box>
          {hasMore && (
            <Box ref={bottomRef} display="flex" justifyContent="center" my={2}>
              <Loader color="black" />
            </Box>
          )}
        </Box>
      ) : (
        <Box className="no-record-found-container">No Record Found</Box>
      )}
    </>
  );
};

export default React.memo(HistoryItems);
